// Import necessary libraries and components
import { Box, Grid, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAxiosPrivate } from '../../hooks'
import {
  fetchHL7EventDetails,
  getHL7EventDetails,
  getHL7EventLoading,
  getHL7LogsError,
  getHL7LogsLoading,
  getSelectedLog,
  getHasHl7LogAccess,
} from '../../pages/slices/hl7Log.slice'
import { formatConnectionCode, parseJSONSafely } from '../../utils'
import CopyToClipboard from '../common/CopyToClipboard/CopyToClipboard'
import Loader from '../common/Loader/Loader'
import {
  formatDateTime,
  formatTimeRemaining,
} from '../../pages/HL7historyLogs/utils'
import Blur from '../common/Blur/Blur'
import CountDown from '../common/CountDown/CountDown'
import FacilityName from './FacilityName'
import HL7EventDetailTabs from './Hl7EventDetailTabs'
import {
  CONNECTED_APPLICATION_TYPES,
  CONNECTION_STATUS_MAP,
} from '../../constants/constants'

const HL7EventDetail = ({ logId }) => {
  const dispatch = useDispatch()
  const axiosPrivate = useAxiosPrivate()

  // Selectors for Redux state
  const loading = useSelector(getHL7LogsLoading)
  const error = useSelector(getHL7LogsError)
  const hl7EventLog = useSelector((state) => getSelectedLog(state, logId))
  const isHL7EventLoading = useSelector(getHL7EventLoading)
  const hl7EventDetails = useSelector(getHL7EventDetails)
  const hasHl7LogAccess = useSelector(getHasHl7LogAccess)

  // Function to fetch full HL7 event data
  const fetchHl7EventFullData = useCallback(
    (axiosInstance) => {
      dispatch(
        fetchHL7EventDetails({
          axiosInstance,
          logId,
        })
      )
    },
    [dispatch, logId]
  )

  useEffect(() => {
    if (logId) {
      fetchHl7EventFullData(axiosPrivate)
    }
  }, [fetchHl7EventFullData, axiosPrivate, logId])

  const canShowConnectionDetails =
    hl7EventDetails?.connectedApplication ===
    CONNECTED_APPLICATION_TYPES.GEHC_MUSE

  const parsedConnectionDetails = useMemo(() => {
    return parseJSONSafely(hl7EventDetails?.connectionDetails) || {}
  }, [hl7EventDetails?.connectionDetails])

  const enrollments = useMemo(() => {
    return (parsedConnectionDetails?.enrollments || []).map(
      (enrollment, index) => ({
        ...enrollment,
        id: index,
        code: formatConnectionCode(enrollment.code) || 'N/A',
        startDate: enrollment.startDate,
        endDate: enrollment.endDate,
        connectionStatus:
          CONNECTION_STATUS_MAP[enrollment.connectionStatus] ||
          enrollment.connectionStatus ||
          'N/A',
        timeToExpire:
          enrollment.endDate && dayjs(enrollment.endDate).isAfter(dayjs())
            ? formatTimeRemaining(
                dayjs(new Date()),
                dayjs(enrollment.endDate)
              ) || 'N/A'
            : '-',
        duration: enrollment.duration || 'N/A',
      })
    )
  }, [parsedConnectionDetails?.enrollments])
  const selectedEnrollment = enrollments?.[0] || {}

  // Render loader if data is loading
  if (loading || isHL7EventLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '80vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader />
      </Box>
    )
  }

  // Render error message if there is an error
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error fetching log details: {error.message || 'Unknown error'}
        </Typography>
      </Box>
    )
  }

  // Main render for HL7 Event Details

  return (
    <Box sx={{ height: '80vh', overflowY: 'auto' }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={600}>
            Order number: {hl7EventLog?.orderNumber}
          </Typography>
        </Grid>
        <Grid item container spacing={4}>
          <FacilityName facilityId={hl7EventLog?.kproTeamId} />
          {canShowConnectionDetails ? (
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                MRN
              </Typography>
              <Typography variant="body1">
                <Blur enabled={!hasHl7LogAccess}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {parsedConnectionDetails?.customParticipantID || 'N/A'}
                    <CopyToClipboard
                      text={parsedConnectionDetails?.customParticipantID || ''}
                      disabled={!parsedConnectionDetails?.customParticipantID}
                    />
                  </Box>
                </Blur>
              </Typography>
            </Grid>
          ) : null}

          <Grid item xs={4}>
            <Typography variant="subtitle2" color="textSecondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {formatDateTime(hl7EventLog?.createdAt) || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {canShowConnectionDetails ? (
          <Grid item container spacing={4}>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Connection Code
              </Typography>
              <Typography
                variant="body1 "
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Blur enabled={!hasHl7LogAccess}>
                  {selectedEnrollment?.code || 'N/A'}
                </Blur>
                <CopyToClipboard
                  text={selectedEnrollment?.code || ''}
                  disabled={!selectedEnrollment?.code}
                />
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Connection Code Duration
              </Typography>
              <Typography variant="body1">
                <Blur enabled={!hasHl7LogAccess}>
                  {selectedEnrollment?.duration || 'N/A'}
                </Blur>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Connection Code Status
              </Typography>
              <Typography variant="body1">
                <Blur enabled={!hasHl7LogAccess}>
                  {selectedEnrollment?.connectionStatus || 'N/A'}
                </Blur>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Redeemed On
              </Typography>
              <Typography variant="body1">
                <Blur enabled={!hasHl7LogAccess}>
                  {selectedEnrollment?.startDate
                    ? formatDateTime(selectedEnrollment.startDate)
                    : 'N/A'}
                </Blur>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Expires On
              </Typography>
              <Typography variant="body1">
                <Blur enabled={!hasHl7LogAccess}>
                  {selectedEnrollment?.endDate
                    ? formatDateTime(selectedEnrollment.endDate)
                    : 'N/A'}
                </Blur>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Expires In
              </Typography>
              <Typography variant="body1" fontWeight="600">
                <Blur enabled={!hasHl7LogAccess}>
                  <CountDown
                    endDateTime={selectedEnrollment?.endDate}
                    onComplete={() => {
                      // Handle countdown completion
                    }}
                  />
                </Blur>
              </Typography>
            </Grid>
          </Grid>
        ) : null}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <HL7EventDetailTabs logId={logId} />
      </Box>
    </Box>
  )
}

export default HL7EventDetail
